import { useCallback, useEffect, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { Attachment } from '../../../../../../models/Attachment';
import { validateAttachments, createAttachmentFromFile } from '../../../../../../utils/attachmentValidation';
import {
  completeAttachmentUpload,
  getAttachmentStatus,
  startAttachmentUpload
} from '../../../../../../services/uploadAttachment';
import { ATTACHMENT_SCAN_MESSAGES } from './attachmentUploadErrors';

// headers managed by the browser, passing them explicitly causes CORS errors
const BROWSER_MANAGED_HEADERS = ['content-length', 'host'];

/**
 * Parses the signed header names from the pre-signed S3 URL
 * Only headers listed in X-Amz-SignedHeaders are part of the signature and have to be included
 */
const getSignedHeaderNames = (uploadUrl: string): Set<string> => {
  const signedHeadersParam = new URL(uploadUrl).searchParams.get('X-Amz-SignedHeaders');

  if (!signedHeadersParam) return new Set();

  return new Set(
    signedHeadersParam
      .split(';')
      .map(h => h.trim().toLowerCase())
      .filter(Boolean)
  );
};

/**
 * Returns only the headers that are signed and safe to send (i.e. not browser-managed).
 */
const createUploadHeaders = (uploadUrl: string, uploadHeaders?: Record<string, string>): HeadersInit => {
  if (!uploadHeaders) return {};

  const signedHeaders = getSignedHeaderNames(uploadUrl);

  return Object.fromEntries(
    Object.entries(uploadHeaders).filter(([name]) => {
      const normalized = name.toLowerCase();
      return signedHeaders.has(normalized) && !BROWSER_MANAGED_HEADERS.includes(normalized);
    })
  );
};

const uploadFileToPresignedUrl = async (
  uploadUrl: string,
  file: File,
  uploadHeaders?: Record<string, string>
): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: createUploadHeaders(uploadUrl, uploadHeaders),
    body: file
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Attachment upload responded with status ${response.status}: ${errorBody}`);
  }
};

/** Strips the query string from a pre-signed S3 URL to get the stable S3 object URL. */
const getS3UrlFromUploadUrl = (uploadUrl: string): string => uploadUrl.split('?')[0];

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

const waitForScanApproval = async (contactId: string, fileId: string): Promise<void> => {
  const timeoutAt = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < timeoutAt) {
    const { status } = await getAttachmentStatus(contactId, fileId);

    if (status === 'approved') return;
    if (status === 'rejected') {
      throw new Error(ATTACHMENT_SCAN_MESSAGES.REJECTED);
    }
    if (status === 'failed') {
      throw new Error('Attachment scan failed');
    }

    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
  }

  throw new Error('Attachment scan failed to complete');
};

const uploadSingleFile = async (file: File, contactId: string): Promise<Attachment> => {
  const baseAttachment = createAttachmentFromFile(file);
  const { fileId, uploadUrl, uploadHeaders } = await startAttachmentUpload(contactId, file);

  await uploadFileToPresignedUrl(uploadUrl, file, uploadHeaders);
  await completeAttachmentUpload(contactId, fileId);
  await waitForScanApproval(contactId, fileId);

  return { ...baseAttachment, fileId, s3Url: getS3UrlFromUploadUrl(uploadUrl) };
};

export interface PendingUpload {
  id: string;
  name: string;
  status: 'uploading' | 'error';
}

interface UseAttachmentUploadProps {
  selectedEmail: string | null;
  uploadContactId?: string | null;
  attachments: Attachment[];
  updateEmailDraft: (contactId: string, draft: { attachments?: Attachment[] }) => void;
  onUploadError?: (message: string) => void;
}

interface UseAttachmentUploadReturn {
  attachmentError: string | null;
  pendingUploads: PendingUpload[];
  isUploadingAttachments: boolean;
  handleFilesSelected: (files: FileList) => Promise<void>;
  handleRemoveAttachment: (attachmentId: string) => void;
  dismissUpload: (id: string) => void;
  clearAttachmentError: () => void;
}

export const useAttachmentUpload = ({
  selectedEmail,
  uploadContactId,
  attachments,
  updateEmailDraft,
  onUploadError
}: UseAttachmentUploadProps): UseAttachmentUploadReturn => {
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);

  // Approved attachments accumulate per initiating email, so a late or concurrent
  // upload completion appends to the draft that started it - never whichever email
  // happens to be selected when the (up to 60s) scan finally resolves.
  const approvedByEmailRef = useRef<Record<string, Attachment[]>>({});

  useEffect(() => {
    setAttachmentError(null);
    setPendingUploads([]);
  }, [selectedEmail]);

  const clearAttachmentError = useCallback((): void => {
    setAttachmentError(null);
  }, []);

  const dismissUpload = useCallback((id: string): void => {
    setPendingUploads(prev => prev.filter(p => p.id !== id));
  }, []);

  const handleFileUpload = useCallback(
    async (file: File, contactId: string, id: string, targetEmail: string): Promise<void> => {
      try {
        const uploaded = await uploadSingleFile(file, contactId);

        const next = [...(approvedByEmailRef.current[targetEmail] ?? []), uploaded];
        approvedByEmailRef.current[targetEmail] = next;
        updateEmailDraft(targetEmail, { attachments: next });
        setPendingUploads(prev => prev.filter(p => p.id !== id));
      } catch (error) {
        datadogLogs.logger.error('Failed to upload attachment', { channel: 'email' }, error as Error);

        if (error instanceof Error && error.message === ATTACHMENT_SCAN_MESSAGES.REJECTED) {
          setAttachmentError(ATTACHMENT_SCAN_MESSAGES.REJECTED);
          onUploadError?.(ATTACHMENT_SCAN_MESSAGES.REJECTED);
          setPendingUploads(prev => prev.filter(p => p.id !== id));
        } else {
          setPendingUploads(prev => prev.map(p => (p.id === id ? { ...p, status: 'error' } : p)));
        }
      }
    },
    [onUploadError, updateEmailDraft]
  );

  const handleFilesSelected = useCallback(
    async (files: FileList): Promise<void> => {
      const { validFiles, error } = validateAttachments(files, attachments);

      setAttachmentError(error ?? null);

      if (validFiles.length === 0 || !selectedEmail || !uploadContactId) {
        return;
      }

      // Pin this batch to the email that started it and seed its accumulator from
      // that draft's current attachments, so completions never depend on the selection.
      const targetEmail = selectedEmail;
      approvedByEmailRef.current[targetEmail] = attachments;

      const entries = validFiles.map(file => ({ id: crypto.randomUUID(), file }));

      setPendingUploads(prev => [
        ...prev,
        ...entries.map(({ id, file }): PendingUpload => ({ id, name: file.name, status: 'uploading' }))
      ]);

      await Promise.all(entries.map(({ id, file }) => handleFileUpload(file, uploadContactId, id, targetEmail)));
    },
    [attachments, selectedEmail, uploadContactId, handleFileUpload]
  );

  const handleRemoveAttachment = useCallback(
    (attachmentId: string): void => {
      if (selectedEmail) {
        updateEmailDraft(selectedEmail, {
          attachments: attachments.filter(a => a.id !== attachmentId)
        });
      }

      setAttachmentError(null);
    },
    [attachments, selectedEmail, updateEmailDraft]
  );

  return {
    attachmentError,
    pendingUploads,
    isUploadingAttachments: pendingUploads.some(p => p.status === 'uploading'),
    handleFilesSelected,
    handleRemoveAttachment,
    dismissUpload,
    clearAttachmentError
  };
};
